import dayjs from "dayjs";
import { ActivityType, Interval } from "../model/activity.model";

export interface StructuredInterval {
  interval: Interval;
  isOccurrence: boolean;
  nestedOccurrences: Interval[];
}

/**
 * Check if an interval belongs to an occurrence-type activity.
 */
export function isOccurrenceInterval(
  interval: Interval,
  activityTypesMap: Map<string, ActivityType>
): boolean {
  const type = activityTypesMap.get(interval.typeId);
  return !!type?.occurrence;
}

/**
 * Format occurrence time as HH:mm from its start.
 */
export function formatOccurrenceTime(interval: Interval): string {
  return dayjs(interval.start).format('HH:mm');
}

/**
 * Takes a flat list of intervals and an activity-types map,
 * separates occurrences from regular intervals, nests occurrences
 * inside regular intervals whose time range contains them,
 * and returns standalone occurrences for the rest.
 *
 * Returned array preserves the original order of regular intervals,
 * with standalone occurrences inserted at their chronological position.
 */
export function structureIntervalsWithOccurrences(
  intervals: Interval[],
  activityTypesMap: Map<string, ActivityType>
): StructuredInterval[] {
  const regular: Interval[] = [];
  const occurrences: Interval[] = [];

  for (const interval of intervals) {
    if (isOccurrenceInterval(interval, activityTypesMap)) {
      occurrences.push(interval);
    } else {
      regular.push(interval);
    }
  }

  // Build structured list for regular intervals
  const structured: StructuredInterval[] = regular.map(interval => ({
    interval,
    isOccurrence: false,
    nestedOccurrences: [],
  }));

  // Track which occurrences have been nested
  const nested = new Set<string>();

  for (const occ of occurrences) {
    const occStart = new Date(occ.start).getTime();

    // Find a regular interval whose [start, finish] contains the occurrence time
    for (const item of structured) {
      const parentStart = new Date(item.interval.start).getTime();
      const parentFinish = new Date(item.interval.finish).getTime();

      if (occStart >= parentStart && occStart < parentFinish) {
        item.nestedOccurrences.push(occ);
        nested.add(occ.id);
        break;
      }
    }
  }

  // Standalone occurrences (not nested inside any regular interval)
  const standalone: StructuredInterval[] = occurrences
    .filter(occ => !nested.has(occ.id))
    .map(interval => ({
      interval,
      isOccurrence: true,
      nestedOccurrences: [],
    }));

  // Merge: keep original order for regular items,
  // insert standalone occurrences at chronological position
  const result: StructuredInterval[] = [];
  let sIdx = 0;

  for (const item of structured) {
    // Insert any standalone occurrences that come before this regular interval
    while (
      sIdx < standalone.length &&
      new Date(standalone[sIdx].interval.start).getTime() >=
        new Date(item.interval.start).getTime()
    ) {
      result.push(standalone[sIdx]);
      sIdx++;
    }
    result.push(item);
  }

  // Append remaining standalone occurrences
  while (sIdx < standalone.length) {
    result.push(standalone[sIdx]);
    sIdx++;
  }

  return result;
}
